import { AfterViewChecked, Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { NotificationsService } from 'src/app/services/notifications.service';
import { NotificationGroup, NotificationRow, NotificationsView } from 'src/app/services/models/notification.model';

/** The order the panel shows them in, and the only labels it will render. */
const GROUP_ORDER: NotificationRow['group'][] = ['Today', 'Yesterday', 'Earlier'];

@Component({
  selector: 'app-notifications-panel',
  templateUrl: './notifications-panel.component.html',
  styleUrls: ['./notifications-panel.component.scss'],
})
export class NotificationsPanelComponent implements AfterViewChecked {
  /** Driven by the shell so the bell owns the open state and the panel stays presentational. */
  @Input() open = false;

  @Output() closed = new EventEmitter<void>();

  /**
   * The close button, which is where focus goes when the panel opens.
   *
   * Chosen over the panel container because it is a real control: a keyboard user lands on something
   * they can act on, and the next Tab walks the rows. Focusing the `<aside>` would announce the
   * dialog and then require an extra Tab to reach anything.
   */
  @ViewChild('closeButton') closeButton?: ElementRef<HTMLButtonElement>;

  /**
   * Whether focus has already been moved for the current opening.
   *
   * `AfterViewChecked` runs on every change-detection pass, and the panel's content is behind
   * `*ngIf`s — so this is the flag that makes focus move ONCE per open, instead of yanking it back
   * to the close button every time a notification arrives while the panel is showing.
   */
  private focusMoved = false;

  /**
   * One view model, one `| async`, one Firestore listener.
   *
   * Built as a single stream rather than three because each `| async` in a template opens its own
   * subscription: three of them would put three live listeners on the same collection and pay for
   * the same reads three times.
   *
   * Empty groups are dropped rather than rendered with a header over nothing — a "YESTERDAY"
   * heading above blank space reads as a loading bug.
   */
  readonly view$: Observable<NotificationsView> = this.notifications.myNotifications$().pipe(
    map((rows) => ({
      rows,
      groups: GROUP_ORDER.map((label) => ({ label, items: rows.filter((row) => row.group === label) })).filter((group) => group.items.length > 0),
      unread: rows.filter((row) => !row.read).length,
    })),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  constructor(private notifications: NotificationsService) {}

  /**
   * Move focus into the panel once it has actually rendered.
   *
   * `AfterViewChecked` rather than `ngOnChanges`: when `open` flips, the content behind `*ngIf` does
   * not exist yet, so the close button is not there to focus. This runs after the view is in place,
   * and the flag keeps it to one move per opening.
   *
   * Focus RETURNS to the bell in the shell, not here — the shell owns that element, and a panel
   * reaching up to focus something it does not own is how two components end up fighting over it.
   */
  ngAfterViewChecked(): void {
    if (!this.open) {
      this.focusMoved = false;

      return;
    }

    if (!this.focusMoved && this.closeButton) {
      this.closeButton.nativeElement.focus();
      this.focusMoved = true;
    }
  }

  /**
   * Clicking a row marks it read.
   *
   * Not awaited: the row is already grey by the time the write lands, because the collection is live
   * and the local write is latency-compensated. Awaiting it would only add a spinner to something
   * the user has already seen happen.
   *
   * But the rejection IS handled, which is different from not awaiting. The write can be refused —
   * the collection's rule allows only `read` and `readAt`, so a future refactor reintroducing a
   * stray field trips it, and an offline or over-quota client fails too. Firestore then rolls the
   * optimistic change back and the row turns unread again. Swallowing that by omission leaves an
   * unhandled rejection and no explanation anywhere; swallowing it deliberately at least leaves the
   * reason in the console.
   */
  onRowClick(row: NotificationRow): void {
    // Marks read and nothing else. The row carries `ref` — the discharge it is about — but nothing
    // navigates to it yet, so clicking a notification about a patient does not open that patient.
    // That is a gap a nurse will notice, and it is left to a follow-up rather than guessed at here:
    // the destination is a product decision (the chart? the discharge? a filtered call list?) and
    // wiring the wrong one is worse than wiring none.
    if (!row.read) {
      this.notifications.markRead(row.id).catch((error) => this.reportWriteFailure('markRead', error));
    }
  }

  /**
   * Keyboard equivalent of the click, because the row is a `div` and not a `button`.
   *
   * Enter and Space are what a `role="button"` promises: announcing the role without handling the
   * keys is the worse half of the bargain, since a screen reader then tells the user it is a button
   * and it does nothing. `preventDefault` on Space stops the panel scrolling underneath the press.
   */
  onRowKeydown(event: KeyboardEvent, row: NotificationRow): void {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    this.onRowClick(row);
  }

  /**
   * Takes the rows the template already has.
   *
   * A pipe cannot appear in an event binding, so the rows arrive as an argument rather than being
   * resolved here — and that is the better shape anyway: the user is acting on what they can see,
   * and re-reading the collection could sweep a row that arrived a second ago and was never on
   * screen.
   */
  onMarkAllRead(rows: NotificationRow[]): void {
    this.notifications.markAllRead(rows).catch((error) => this.reportWriteFailure('markAllRead', error));
  }

  /** Escape closes the panel, the one keyboard affordance every dialog is expected to have. */
  onEscape(): void {
    if (this.open) {
      this.closed.emit();
    }
  }

  /** `trackBy` so marking one row read does not re-create the other twenty-nine DOM nodes. */
  trackById(_index: number, row: NotificationRow): string {
    return row.id;
  }

  trackByLabel(_index: number, group: NotificationGroup): string {
    return group.label;
  }

  /**
   * The error and nothing else. The row carries the patient's name in `description`, which is PHI —
   * logging the object to explain a failed write would put it in the browser console and in any
   * error tracker reading from there.
   */
  private reportWriteFailure(method: string, error: unknown): void {
    console.error(`[notifications] ${method} failed`, error);
  }
}
